/**
 * TranscriptViewer — SPEC-013 · Transcript Quality UX
 *
 * Renders the formatted transcript in one of four modes:
 *   dialogue  — [MM:SS] **Speaker N:** text (default, current format)
 *   paragraph — consecutive same-speaker lines grouped; no per-line timestamp
 *   verbatim  — raw text only; no timestamps, no speaker labels
 *   review    — dialogue + amber highlight on words with confidence < 0.7
 *
 * Props:
 *   transcript  — the formatted string from the API (dialogue format)
 *   utterances  — optional word-level data for review mode
 *   mode        — display mode
 *   onEdit      — optional callback when user edits a word in review mode
 */

import { useMemo } from 'react';
import type { TranscriptUtterance, TranscriptViewMode } from '../types';

interface TranscriptViewerProps {
  transcript: string;
  utterances?: TranscriptUtterance[] | null;
  mode: TranscriptViewMode;
  onEdit?: (newTranscript: string) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Extract the plain text from a formatted transcript line */
function stripLineMarkup(line: string): string {
  return line
    .replace(/^\[\d{1,2}:\d{2}\]\s*/, '') // strip [MM:SS]
    .replace(/^\*\*.*?\*\*:?\s*/, '')       // strip **Speaker N:** or **Speaker N**:
    .trim();
}

/** Parse a formatted dialogue line into { time?, speaker?, text } */
function parseLine(line: string): { time: string | null; speaker: string | null; text: string } {
  const timeMatch = line.match(/^\[(\d{1,2}:\d{2})\]/);
  const time = timeMatch ? timeMatch[1] : null;
  const withoutTime = timeMatch ? line.slice(timeMatch[0].length).trim() : line;

  const speakerMatch = withoutTime.match(/^\*\*(.*?)\*\*:?\s*/);
  const speaker = speakerMatch ? speakerMatch[1] : null;
  const text = speakerMatch ? withoutTime.slice(speakerMatch[0].length).trim() : withoutTime;

  return { time, speaker, text };
}

// ── Mode: dialogue ────────────────────────────────────────────────────────────

function DialogueView({ transcript }: { transcript: string }) {
  const lines = transcript.split('\n').filter(l => l.trim());

  return (
    <div className="space-y-3 sm:space-y-4">
      {lines.map((line, idx) => {
        const parts = line.split(/(\[\d+:\d+\])/g);
        return (
          <div
            key={idx}
            className="group relative border-l-2 border-transparent hover:border-[#00d4b8]/40 transition-colors pl-4 animate-in"
            style={{ animationDelay: `${Math.min(idx * 20, 500)}ms` }}
          >
            <div className="absolute -left-12 top-0 text-[10px] text-slate-300 font-mono opacity-0 group-hover:opacity-100 transition-opacity select-none">
              LN {String(idx + 1).padStart(3, '0')}
            </div>
            {parts.map((part, pidx) => {
              if (part.match(/\[\d+:\d+\]/)) {
                return (
                  <span
                    key={pidx}
                    className="text-[#00d4b8] font-black bg-[#00d4b8]/10 px-2 py-0.5 rounded mx-1 shadow-sm border border-[#00d4b8]/20"
                    role="time"
                  >
                    {part}
                  </span>
                );
              }
              if (part.includes('**')) {
                const subParts = part.split(/(\*\*.*?\*\*)/g);
                return subParts.map((sp, sidx) => {
                  if (sp.startsWith('**') && sp.endsWith('**')) {
                    return (
                      <span
                        key={sidx}
                        className="text-[#1a2332] font-bold tracking-tight underline decoration-[#00d4b8]/50 decoration-2 underline-offset-4"
                      >
                        {sp.replace(/\*\*/g, '')}
                      </span>
                    );
                  }
                  return sp;
                });
              }
              return part;
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── Mode: paragraph ───────────────────────────────────────────────────────────

function ParagraphView({ transcript }: { transcript: string }) {
  const lines = transcript.split('\n').filter(l => l.trim());

  // Group consecutive same-speaker lines
  const groups: Array<{ speaker: string | null; lines: string[]; firstTime: string | null }> = [];
  for (const line of lines) {
    const { time, speaker, text } = parseLine(line);
    const last = groups[groups.length - 1];
    if (last && last.speaker === speaker) {
      last.lines.push(text);
    } else {
      groups.push({ speaker, lines: [text], firstTime: time });
    }
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {groups.map((grp, idx) => (
        <div key={idx} className="animate-in" style={{ animationDelay: `${Math.min(idx * 30, 500)}ms` }}>
          {grp.speaker && (
            <div className="flex items-center gap-3 mb-2">
              {grp.firstTime && (
                <span className="text-[10px] text-slate-400 font-mono">[{grp.firstTime}]</span>
              )}
              <span className="text-[#1a2332] font-bold text-[12px] sm:text-[13px] underline decoration-[#00d4b8]/50 decoration-2 underline-offset-4">
                {grp.speaker}
              </span>
            </div>
          )}
          <p className="text-[13px] sm:text-[14px] leading-relaxed text-slate-600 pl-0">
            {grp.lines.join(' ')}
          </p>
        </div>
      ))}
    </div>
  );
}

// ── Mode: verbatim ────────────────────────────────────────────────────────────

function VerbatimView({ transcript }: { transcript: string }) {
  const lines = transcript.split('\n').filter(l => l.trim());
  const text = lines.map(stripLineMarkup).filter(Boolean).join(' ');

  return (
    <div className="text-[13px] sm:text-[14px] leading-relaxed text-slate-600 whitespace-pre-wrap animate-in">
      {text}
    </div>
  );
}

// ── Mode: review ──────────────────────────────────────────────────────────────

const CONF_THRESHOLD = 0.7;

function ReviewView({ transcript, utterances }: { transcript: string; utterances?: TranscriptUtterance[] | null }) {
  // If we have utterance-level word data, use it for per-word highlighting
  if (utterances && utterances.length > 0) {
    return (
      <div className="space-y-3 sm:space-y-4">
        {utterances.map((u, uidx) => {
          const time = formatTime(u.start);
          return (
            <div
              key={uidx}
              className="group relative border-l-2 border-transparent hover:border-[#00d4b8]/40 transition-colors pl-4 animate-in"
              style={{ animationDelay: `${Math.min(uidx * 20, 500)}ms` }}
            >
              <div className="flex flex-wrap items-baseline gap-x-1">
                <span className="text-[#00d4b8] font-black bg-[#00d4b8]/10 px-2 py-0.5 rounded mr-1 shadow-sm border border-[#00d4b8]/20 text-[11px] sm:text-[12px] shrink-0" role="time">
                  [{time}]
                </span>
                <span className="text-[#1a2332] font-bold underline decoration-[#00d4b8]/50 decoration-2 underline-offset-4 mr-1 text-[12px] sm:text-[13px] shrink-0">
                  Speaker {u.speaker + 1}:
                </span>
                {u.words.length > 0
                  ? u.words.map((w, widx) => {
                      const isLowConf = w.confidence < CONF_THRESHOLD;
                      return (
                        <span
                          key={widx}
                          className={`inline text-[12px] sm:text-[13px] ${isLowConf ? 'bg-amber-100 text-amber-800 rounded px-0.5' : 'text-slate-600'}`}
                          title={isLowConf ? `Low confidence: ${Math.round(w.confidence * 100)}%` : undefined}
                        >
                          {w.punctuated_word ?? w.word}
                          {widx < u.words.length - 1 ? ' ' : ''}
                        </span>
                      );
                    })
                  : <span className="text-[12px] sm:text-[13px] text-slate-600">{u.transcript}</span>
                }
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Fallback: render dialogue view (no word-level data available)
  return <DialogueView transcript={transcript} />;
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

// ── Mode toggle buttons ────────────────────────────────────────────────────────

interface ModeToggleProps {
  mode: TranscriptViewMode;
  onChange: (mode: TranscriptViewMode) => void;
  hasUtterances: boolean;
}

const MODES: Array<{ id: TranscriptViewMode; label: string; title: string }> = [
  { id: 'dialogue',  label: 'Dialogue',  title: 'Speaker-labelled lines with timestamps' },
  { id: 'paragraph', label: 'Paragraph', title: 'Consecutive same-speaker lines grouped into paragraphs' },
  { id: 'verbatim',  label: 'Verbatim',  title: 'Plain text only — no timestamps or speaker labels' },
  { id: 'review',    label: 'Review',    title: 'Highlight low-confidence words (< 70%)' },
];

export function TranscriptModeToggle({ mode, onChange, hasUtterances }: ModeToggleProps) {
  return (
    <div className="flex items-center gap-1 flex-wrap" role="group" aria-label="Transcript view mode">
      {MODES.map(m => (
        <button
          key={m.id}
          onClick={() => onChange(m.id)}
          disabled={m.id === 'review' && !hasUtterances}
          className={`px-2 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-all border
            ${mode === m.id
              ? 'bg-[#00d4b8] text-white border-[#00d4b8] shadow-sm'
              : 'bg-white text-slate-500 border-slate-200 hover:border-[#00d4b8]/50 hover:text-[#00d4b8]'}
            ${m.id === 'review' && !hasUtterances ? 'opacity-40 cursor-not-allowed' : ''}
          `}
          title={m.id === 'review' && !hasUtterances ? 'Word-level data not available for this transcript' : m.title}
          aria-pressed={mode === m.id}
        >
          {m.label}
        </button>
      ))}
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export function TranscriptViewer({ transcript, utterances, mode }: TranscriptViewerProps) {
  // Note: outer styling (font-mono, border-l-4, pl-6) provided by the parent container in App.tsx
  return useMemo(() => {
    switch (mode) {
      case 'paragraph': return <ParagraphView transcript={transcript} />;
      case 'verbatim':  return <VerbatimView  transcript={transcript} />;
      case 'review':    return <ReviewView    transcript={transcript} utterances={utterances} />;
      default:          return <DialogueView  transcript={transcript} />;
    }
  }, [transcript, utterances, mode]);
}
